import { finding, inspection, isNull, sql, template } from "@fieldcheck/powersync";
import type { AnySQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ SOFT-DELETE — CENTRALIZED LIVE LAYER (single choke point)              │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * This module is the ONLY place where the invariant "live row = `deleted_at IS
 * NULL`" is expressed for the 3 tables with trash in this repo (verified
 * reading `packages/powersync/src/schema.ts`, not assumed):
 *   inspection, finding, template.
 * Every live read of these tables composes these predicates (the builders in
 * `fieldwork/queries.ts` and any other live view import them from here). The
 * ONLY reads that see deleted rows are those of the trash
 * (`fieldwork/queries.ts#getTrashed*Query`, whitelisted as "trash") and the
 * harmless id-scoped lookups (detail by id / read-before-write, "keyed").
 *
 * Why centralize? The scattered invariant already caused repeated inline
 * filters (`isNull(x.deleted_at)`) in each listing builder + a RAW counter
 * in `checkUnsyncedActor` (counted rows in trash). The static guard
 * (`__tests__/live-access-guard.test.ts` + `guard-manifest.ts`) prevents
 * regression: any new read outside the live layer breaks the build.
 *
 * ─── field-check is B2B IMMUTABLE (compliance) ─────────────────────────────
 * The trash is RESTORE-ONLY: there is no "permanent delete" nor purge
 * job (evidence of inspection/finding/template is never destroyed —
 * the `activity` hash-chain is an append-only audit trail). The live layer
 * is a READ concern, orthogonal to activity history.
 *
 * ─── Cohort hierarchy (FieldCheck domain) ──────────────────────────────────
 * Verified against the LOGICAL FKs in `packages/powersync/src/schema.ts` (there
 * are no physical FKs in PowerSync SQLite: they are modeled logically):
 *   - template   → ROOT (only `organization_id`/`user_id`). Does not depend on any
 *                  other soft-delete table.
 *   - inspection → ROOT. `template_id`→template and `site_id`→site: an inspection
 *                  is an INDEPENDENT record (sealed evidence) that survives
 *                  its template being archived/deleted, and `site` is NOT
 *                  soft-deletable — that is why inspection does not take a parent cohort.
 *   - finding    → child of inspection (`inspection_id`, OPTIONAL: a finding
 *                  can belong to a site without an inspection). If its inspection is in
 *                  the trash, the finding disappears from live views and
 *                  reappears upon restoring the inspection (cohort). With
 *                  `inspection_id` NULL the NOT EXISTS resolves to true (does not
 *                  hide the row).
 *
 * NOTE (one-level cohort, pattern from pilot/comandero/stockpilot): the cohort
 * checks the DIRECT PARENT, without table aliases. The self-join with drizzle alias
 * breaks PowerSync `resolveTables` at runtime (see the gotcha
 * documented in stockpilot): here it does not apply because finding→inspection are
 * distinct tables and the predicate uses the literal name of the parent table.
 */

/**
 * Generic cohort predicate: the child row belongs to a parent that is NOT in
 * the trash. With parent soft-delete the children remain intact (to restore
 * the entire cohort) but disappear from live views. If `childFk` is NULL
 * (optional FK, e.g. `finding.inspection_id` of a site
 * finding), the NOT EXISTS resolves to true — does not hide the row.
 */
function parentNotTrashed(
  parent: SQLiteTable & { id: AnySQLiteColumn; deleted_at: AnySQLiteColumn },
  childFk: AnySQLiteColumn,
) {
  return sql`NOT EXISTS (SELECT 1 FROM ${parent} WHERE ${parent.id} = ${childFk} AND ${parent.deleted_at} IS NOT NULL)`;
}

// ─── Roots (only their own deleted_at) ──────────────────────────────────────

/** LIVE templates (root: does not hang from another soft-delete table). */
export function liveTemplateFilters() {
  return [isNull(template.deleted_at)];
}

/**
 * LIVE inspections (root: an inspection is independent evidence; its
 * template/site does not drag it to the trash).
 */
export function liveInspectionFilters() {
  return [isNull(inspection.deleted_at)];
}

// ─── Children with cohort (parent with trash not deleted) ───────────────────

/**
 * LIVE findings: their own `deleted_at` + their inspection (when it exists) is not
 * in the trash. Reappears upon restoring the inspection.
 */
export function liveFindingFilters() {
  return [isNull(finding.deleted_at), parentNotTrashed(inspection, finding.inspection_id)];
}
